import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Module-level action functions
//
// Defined outside the Zustand creator so they are stable references that
// never change across renders or setState calls.  They access/update state
// via the `_store` forward reference (set after create() below).
//
// This pattern lets tests call  setState(INITIAL, true)  to reset data
// without losing the action methods: the patched getState() (see bottom of
// file) always re-injects the ACTIONS object, so getState().initForUser etc.
// are always functions regardless of what setState was called with.
// ---------------------------------------------------------------------------

let _store; // set after create()

function initForUser(userId) {
  const s = _store.getState();
  if (s.userId !== null && s.userId !== userId) {
    // Different user — wipe all session data
    _store.setState({
      userId,
      sessionActionedIds: [],
      sessionActions: [],
      sessionStartTime: null,
      lastActivityAt: null,
      sessionBarDismissed: false,
      inactivityReminderShown: false,
      primaryConsequenceType: null,
      primaryConsequenceTypeLabel: null,
      sessionRosterRecordIds: [],
      concludedSession: null,
      serverSessionId: null,
      serverSessionRequestId: null,
      serverSessionStatus: null,
      serverSessionRecovery: null,
      serverDeliveryStatus: null,
      concludedServerSessionKeys: [],
    });
  } else {
    _store.setState({ userId });
  }
}

function trackAction(eventId, status, eventData, opts) {
  const s = _store.getState();
  const now = Date.now();

  const entry = [
    eventId,
    {
      status,
      studentName: eventData?.studentName || 'Unknown',
      studentId: eventData?.studentId || null,
      type: eventData?.consequenceType || eventData?.type || 'unknown',
      offence: eventData?.offence || eventData?.offenceName || '',
      className: eventData?.className || '',
      rosterRecordIds: Array.isArray(opts?.rosterRecordIds)
        ? [...new Set(opts.rosterRecordIds.filter(Boolean))]
        : [eventId],
      timestamp: new Date().toISOString(),
    },
  ];

  // Replace existing entry for this eventId, then append
  const filteredActions = s.sessionActions.filter(([id]) => id !== eventId);
  const newActions = [...filteredActions, entry];

  const newIds = s.sessionActionedIds.includes(eventId)
    ? s.sessionActionedIds
    : [...s.sessionActionedIds, eventId];

  const updates = {
    sessionActionedIds: newIds,
    sessionActions: newActions,
    lastActivityAt: now,
    concludedSession: null,
  };

  if (!s.sessionStartTime) {
    updates.sessionStartTime = now;
  }

  if (!Array.isArray(s.sessionRosterRecordIds) || s.sessionRosterRecordIds.length === 0) {
    updates.sessionRosterRecordIds = Array.isArray(opts?.sessionRosterRecordIds)
      ? [...new Set(opts.sessionRosterRecordIds.filter(Boolean))]
      : [...new Set(entry[1].rosterRecordIds)];
  }

  if (!s.primaryConsequenceType && opts?.consequenceType) {
    updates.primaryConsequenceType = opts.consequenceType;
    updates.primaryConsequenceTypeLabel = opts.consequenceTypeLabel || null;
  }

  _store.setState(updates);
}

function untrackAction(eventId) {
  const s = _store.getState();
  _store.setState({
    sessionActionedIds: s.sessionActionedIds.filter((id) => id !== eventId),
    sessionActions: s.sessionActions.filter(([id]) => id !== eventId),
    lastActivityAt: Date.now(),
  });
}

function clearSession() {
  const s = _store.getState();
  _store.setState({
    userId: s.userId,          // preserve the current user
    sessionActionedIds: [],
    sessionActions: [],
    sessionStartTime: null,
    lastActivityAt: null,
    sessionBarDismissed: false,
    inactivityReminderShown: false,
    primaryConsequenceType: null,
    primaryConsequenceTypeLabel: null,
    sessionRosterRecordIds: [],
    serverSessionId: null,
    serverSessionRequestId: null,
    serverSessionStatus: null,
    serverSessionRecovery: null,
    serverDeliveryStatus: null,
  });
}

function concludeSession(details = {}) {
  const s = _store.getState();
  const summary = getSessionSummary();
  _store.setState({
    userId: s.userId,
    sessionActionedIds: [],
    sessionActions: [],
    sessionStartTime: null,
    lastActivityAt: null,
    sessionBarDismissed: false,
    inactivityReminderShown: false,
    primaryConsequenceType: null,
    primaryConsequenceTypeLabel: null,
    sessionRosterRecordIds: [],
    serverSessionId: null,
    serverSessionRequestId: null,
    serverSessionStatus: null,
    serverSessionRecovery: null,
    serverDeliveryStatus: null,
    concludedSession: {
      consequenceType: details.consequenceType || s.primaryConsequenceType,
      consequenceTypeLabel:
        details.consequenceTypeLabel || s.primaryConsequenceTypeLabel || null,
      concludedAt: Date.now(),
      summary,
    },
  });
}

function prepareSession({ consequenceType, consequenceTypeLabel, rosterRecordIds }) {
  const s = _store.getState();
  _store.setState({
    concludedSession: null,
    sessionActionedIds: [],
    sessionActions: [],
    sessionStartTime: Date.now(),
    lastActivityAt: Date.now(),
    sessionBarDismissed: false,
    inactivityReminderShown: false,
    primaryConsequenceType: consequenceType || null,
    primaryConsequenceTypeLabel: consequenceTypeLabel || null,
    sessionRosterRecordIds: Array.isArray(rosterRecordIds)
      ? [...new Set(rosterRecordIds.filter(Boolean))]
      : [],
    serverSessionId: null,
    serverSessionRequestId: null,
    serverSessionStatus: null,
    serverSessionRecovery: null,
    serverDeliveryStatus: null,
    userId: s.userId,
  });
}

function setServerSessionRequest(requestId) {
  _store.setState({ serverSessionRequestId: requestId || null });
}

// Both the app footer and Queue own session-hook instances. Share terminal
// observations so one consumer cannot revive work the other has concluded.
// These keys are scoped to the actor/school and intentionally not persisted.
function markServerSessionConcluded({ schoolId, userId, sessionId }) {
  if (!schoolId || !userId || !sessionId) return;
  const key = JSON.stringify([schoolId, userId, sessionId]);
  const keys = _store.getState().concludedServerSessionKeys || [];
  if (!keys.includes(key)) {
    _store.setState({ concludedServerSessionKeys: [...keys, key] });
  }
}

function hasConcludedServerSession({ schoolId, userId, sessionId }) {
  return Boolean(schoolId && userId && sessionId) &&
    (_store.getState().concludedServerSessionKeys || [])
      .includes(JSON.stringify([schoolId, userId, sessionId]));
}

function timestampMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') {
    return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function syncServerSession(sessionId, session) {
  if (!sessionId || !session) return;
  const actions = Object.entries(session.actions || {}).map(([eventId, action]) => [
    eventId,
    {
      ...action,
      timestamp: timestampMillis(action.recordedAt)
        ? new Date(timestampMillis(action.recordedAt)).toISOString()
        : null,
      rosterRecordIds: [eventId],
    },
  ]);
  _store.setState({
    serverSessionId: sessionId,
    serverSessionRequestId: session.requestId || null,
    serverSessionStatus: session.status || null,
    serverSessionRecovery: session.recovery || null,
    serverDeliveryStatus: session.deliveryStatus || null,
    sessionActionedIds: session.actionedRecordIds || actions.map(([id]) => id),
    sessionActions: actions,
    sessionStartTime: timestampMillis(session.startedAt),
    lastActivityAt: timestampMillis(session.lastActivityAt),
    primaryConsequenceType: session.consequenceType || null,
    primaryConsequenceTypeLabel: session.consequenceTypeLabel || null,
    sessionRosterRecordIds: session.rosterRecordIds || [],
    concludedSession: null,
  });
}

function detachServerSession() {
  _store.setState({
    serverSessionId: null,
    serverSessionRequestId: null,
    serverSessionStatus: null,
    serverSessionRecovery: null,
    serverDeliveryStatus: null,
  });
}

function setSessionBarDismissed(dismissed) {
  _store.setState({ sessionBarDismissed: dismissed });
}

function markInactivityReminderShown() {
  _store.setState({ inactivityReminderShown: true });
}

function getSessionSummary() {
  const state = _store.getState();
  const counts = { attended: 0, missed: 0, late: 0, absent: 0, other: 0, total: 0 };
  state.sessionActions.forEach(([, action]) => {
    counts.total++;
    switch (action.status) {
      case 'served':
        counts.attended++;
        break;
      case 'missed':
        counts.missed++;
        break;
      case 'late':
        counts.late++;
        break;
      case 'absent':
        counts.absent++;
        break;
      default:
        counts.other++;
    }
  });
  return counts;
}

function isSessionActive() {
  const state = _store.getState();
  return Boolean(state.serverSessionId) || state.sessionActions.length > 0;
}

const ACTIONS = {
  initForUser,
  trackAction,
  untrackAction,
  clearSession,
  concludeSession,
  prepareSession,
  setServerSessionRequest,
  markServerSessionConcluded,
  hasConcludedServerSession,
  syncServerSession,
  detachServerSession,
  setSessionBarDismissed,
  markInactivityReminderShown,
  getSessionSummary,
  isSessionActive,
};

// ---------------------------------------------------------------------------
// Zustand store with localStorage persistence
// ---------------------------------------------------------------------------

const useSessionTrackingStore = create(
  persist(
    // State creator: returns initial data + stable action references
    (_set, _get) => ({
      // Persisted data fields
      userId: null,
      sessionActionedIds: [],          // Array<string>
      sessionActions: [],              // Array<[eventId, ActionMeta]>
      sessionStartTime: null,          // Date.now() on first action
      lastActivityAt: null,            // Date.now() on every track/untrack
      sessionBarDismissed: false,
      inactivityReminderShown: false,
      primaryConsequenceType: null,
      primaryConsequenceTypeLabel: null,
      sessionRosterRecordIds: [],       // Full register captured when the session starts
      concludedSession: null,           // Operator-only view after a saved conclusion
      serverSessionId: null,            // Durable Firestore action-session ID
      serverSessionRequestId: null,     // Stable callable retry identity
      serverSessionStatus: null,
      serverSessionRecovery: null,
      serverDeliveryStatus: null,
      concludedServerSessionKeys: [],
      // Actions (module-level stable functions)
      ...ACTIONS,
    }),
    {
      name: 'swan-session-tracking',
      // Functions are not serialisable — persist only saves data fields.
      // On rehydration, Zustand merges stored data with the initial state
      // (which includes ACTIONS), so actions are always available in the app.
      partialize: (state) => {
        const { /* actions omitted */ ...data } = state;
        // Explicitly pick only serialisable data keys
        return {
          userId: data.userId,
          sessionActionedIds: data.sessionActionedIds,
          sessionActions: data.sessionActions,
          sessionStartTime: data.sessionStartTime,
          lastActivityAt: data.lastActivityAt,
          sessionBarDismissed: data.sessionBarDismissed,
          inactivityReminderShown: data.inactivityReminderShown,
          primaryConsequenceType: data.primaryConsequenceType,
          primaryConsequenceTypeLabel: data.primaryConsequenceTypeLabel,
          sessionRosterRecordIds: data.sessionRosterRecordIds,
          concludedSession: data.concludedSession,
          serverSessionId: data.serverSessionId,
          serverSessionRequestId: data.serverSessionRequestId,
          serverSessionStatus: data.serverSessionStatus,
          serverSessionRecovery: data.serverSessionRecovery,
          serverDeliveryStatus: data.serverDeliveryStatus,
        };
      },
    }
  )
);

// Resolve the forward reference used by the module-level action functions
_store = useSessionTrackingStore;

// ---------------------------------------------------------------------------
// Patch getState() to always inject action functions.
//
// In tests, resetStore() calls  setState(INITIAL, true)  (full replace in
// Zustand v5) which wipes the actions from the raw state object.  By
// wrapping getState() we ensure action methods are always present on the
// returned object regardless of how tests reset data.
//
// In the production app, setState(INITIAL, true) is never called, so the
// raw state retains actions from the initial creator call.  The patch is a
// no-op there (actions are already present in rawState and in ACTIONS).
// ---------------------------------------------------------------------------
const _rawGetState = useSessionTrackingStore.getState;
useSessionTrackingStore.getState = () => ({
  ..._rawGetState(),
  ...ACTIONS,
});

export default useSessionTrackingStore;
